import { TypeFlight, TypeSegmentAmenities } from "@/lib/redux/features/flight/type"

export type AmenityCategory = keyof TypeSegmentAmenities
export type AmenityStatus = 'included' | 'fee' | 'none' | 'info'

export type AmenityItem = {
  category: AmenityCategory
  labelVi: string
  labelEn: string
  valueVi: string
  valueEn: string
  status: AmenityStatus
}

// Thứ tự hiển thị: nhóm dịch vụ trước, thông tin máy bay/ghế sau.
export const AMENITY_ORDER: AmenityCategory[] = [
  'food', 'beverage', 'entertainment', 'wifi', 'power', 'aircraft', 'layout', 'seat'
]

// Ánh xạ tên nhóm ở campaign_label (API) -> field trong TypeSegmentAmenities.
export const AMENITY_CATEGORY_MAP: Record<string, AmenityCategory> = {
  Aircraft: 'aircraft',
  Beverage: 'beverage',
  Entertainment: 'entertainment',
  Food: 'food',
  Layout: 'layout',
  Power: 'power',
  Seat: 'seat',
  WiFi: 'wifi',
}

// Nhóm chỉ mang tính thông tin (không phân biệt miễn phí/tính phí).
const INFO_CATEGORIES: AmenityCategory[] = ['aircraft', 'layout', 'seat']

const CATEGORY_LABEL: Record<AmenityCategory, { vi: string, en: string }> = {
  aircraft: { vi: 'Máy bay', en: 'Aircraft' },
  beverage: { vi: 'Đồ uống', en: 'Beverage' },
  entertainment: { vi: 'Giải trí', en: 'Entertainment' },
  food: { vi: 'Suất ăn', en: 'Meal' },
  layout: { vi: 'Sơ đồ ghế', en: 'Seat layout' },
  power: { vi: 'Cổng sạc', en: 'Power' },
  seat: { vi: 'Khoảng cách ghế', en: 'Seat' },
  wifi: { vi: 'WiFi', en: 'Wi-Fi' },
}

// Dịch cố định các value không theo pattern số.
const EXACT_VI: Record<string, string> = {
  // Beverage
  'Alcohol & beverages (fee)': 'Đồ uống có cồn & giải khát (tính phí)',
  'Alcohol & beverages provided': 'Đồ uống có cồn & giải khát miễn phí',
  'Alcohol (fee) & beverages provided': 'Đồ uống có cồn (tính phí) & giải khát miễn phí',
  'Nonalcoholic beverages provided': 'Đồ uống không cồn miễn phí',
  'Premium alcohol & beverages provided': 'Đồ uống có cồn cao cấp miễn phí',
  // Entertainment
  'No entertainment': 'Không có giải trí',
  'Seatback on-demand & live TV': 'Màn hình sau ghế: theo yêu cầu & TV trực tiếp',
  'Seatback on-demand video': 'Màn hình sau ghế: video theo yêu cầu',
  'Streaming entertainment': 'Giải trí phát trực tuyến',
  'Streaming on-demand & live TV': 'Phát trực tuyến theo yêu cầu & TV trực tiếp',
  // Food
  'Breakfast provided': 'Phục vụ bữa sáng',
  'Light meal (fee)': 'Bữa ăn nhẹ (tính phí)',
  'Light meal provided': 'Phục vụ bữa ăn nhẹ',
  'Lunch provided': 'Phục vụ bữa trưa',
  'Meal provided': 'Phục vụ suất ăn',
  'Meals provided': 'Phục vụ suất ăn',
  'Snacks provided': 'Phục vụ đồ ăn nhẹ',
  // Power
  'Chance of USB outlet': 'Có thể có cổng USB',
  'Chance of power & USB outlet': 'Có thể có cổng nguồn & USB',
  'Power & USB outlets': 'Cổng nguồn & USB',
  'USB outlet': 'Cổng USB',
  // Seat (loại không phải pitch)
  'Adjacent seat unoccupied (84 cm)': 'Ghế bên cạnh để trống (84 cm)',
  'Cradle recliner (94 cm)': 'Ghế ngả kiểu nôi (94 cm)',
  'Cradle recliner (97 cm)': 'Ghế ngả kiểu nôi (97 cm)',
  'Cradle recliner (102 cm)': 'Ghế ngả kiểu nôi (102 cm)',
  'Cradle recliner (150 cm)': 'Ghế ngả kiểu nôi (150 cm)',
  'Recliner seat (94 cm)': 'Ghế ngả (94 cm)',
  'Recliner seat (97 cm)': 'Ghế ngả (97 cm)',
  'Full flat': 'Ghế ngả phẳng 180°',
  'Full flat all aisle access': 'Ghế ngả phẳng, lối đi riêng',
  'Private suite': 'Khoang riêng',
  // WiFi
  'Basic web browsing': 'Duyệt web cơ bản',
  'Basic web browsing (fee)': 'Duyệt web cơ bản (tính phí)',
  'Chance of Wi-Fi (fee)': 'Có thể có Wi-Fi (tính phí)',
  'Chance of ultra-fast Wi-Fi': 'Có thể có Wi-Fi siêu nhanh',
  'Fast web browsing': 'Duyệt web nhanh',
  'Fast web browsing (fee)': 'Duyệt web nhanh (tính phí)',
  'No Wi-Fi': 'Không có Wi-Fi',
  'Partial route Wi-Fi': 'Wi-Fi một phần hành trình',
  'Streaming Capable Wi-Fi': 'Wi-Fi xem trực tuyến',
  'Streaming Capable Wi-Fi (fee)': 'Wi-Fi xem trực tuyến (tính phí)',
  'Ultra-fast Wi-Fi': 'Wi-Fi siêu nhanh',
}

const getStatus = (category: AmenityCategory, valueEn: string): AmenityStatus => {
  if (INFO_CATEGORIES.includes(category)) return 'info'
  if (/^no\b/i.test(valueEn)) return 'none'
  if (/\(fee\)|chance/i.test(valueEn)) return 'fee'
  return 'included'
}

// Dịch value EN sang VI: ưu tiên pattern (bao biến thể số), còn lại tra map cố định.
const translateValueVi = (category: AmenityCategory, valueEn: string): string => {
  const layout = valueEn.match(/^(.+?)\s+seat layout$/i)
  if (layout) return `Sơ đồ ghế ${layout[1]}`

  const pitch = valueEn.match(/^(\d+)\s*cm seat pitch$/i)
  if (pitch) return `Khoảng cách ghế ${pitch[1]} cm`

  if (category === 'aircraft') {
    return valueEn
      .replace(/\(widebody\)/i, '(thân rộng)')
      .replace(/\(narrowbody\)/i, '(thân hẹp)')
      .replace(/\(jumbo\)/i, '(siêu lớn)')
  }

  return EXACT_VI[valueEn] || valueEn
}

/**
 * Lấy danh sách tiện ích cần hiển thị cho 1 chặng bay (theo vị trí segment).
 * amenities được callback bơm từ e4t.campaign_label vào flight, index khớp segment.
 */
export const getSegmentAmenities = (
  flight?: TypeFlight | null,
  segmentIndex?: number
): AmenityItem[] => {
  if (!flight?.amenities?.length || segmentIndex == null) return []
  const segAmenities = flight.amenities[segmentIndex]
  if (!segAmenities) return []

  const items: AmenityItem[] = []
  for (const category of AMENITY_ORDER) {
    const valueEn = segAmenities[category]
    if (!valueEn) continue
    items.push({
      category,
      labelVi: CATEGORY_LABEL[category].vi,
      labelEn: CATEGORY_LABEL[category].en,
      valueVi: translateValueVi(category, valueEn),
      valueEn,
      status: getStatus(category, valueEn),
    })
  }
  return items
}
